// Third-party
import React from 'react';
import classNames from 'classnames';

// Components
import PrimaryLink from '../buttons/PrimaryLink';
import { CorrelationChart } from '../charts';
import { InfoTooltip } from '../tooltips';

// Utils
import { FormattedMessage } from '../../../utils/i18n';
import ComparisonItem from '@/types/comparison-item';
import RiskPeriods from '@/types/risk-periods';
import DisclaimerInfos from '@/types/disclaimer-infos';
import Comparison from '@/types/comparison';
import { getCorrelationMatrix } from '@/utils/comparison/portfolio-comparison-item';
import { comparisonIsReadOnly } from '@/utils/comparison';

type Props = {
  comparison: Comparison;
  value: string;
  item: ComparisonItem;
  riskPeriod: RiskPeriods;
  className?: string;
  onClick?: () => void;
  linkDisabled: boolean;
  id?: string;
  onShowCorrelation: (comparisonItem: ComparisonItem) => void;
  valueDisclaimer?: DisclaimerInfos;
};

type State = {
  modalIsOpen: boolean;
};

class CorrelationCell extends React.Component<Props, State> {
  readonly MAX_DISPLAYED_HOLDINGS = 5;

  openModal = (event: React.MouseEvent) => {
    if (this.props.linkDisabled) {
      return;
    }

    event.stopPropagation();
    this.props.onShowCorrelation(this.props.item);
  };

  getCorrelationMatrix = () => {
    const { comparison, item, riskPeriod } = this.props;
    // Note on shortHistory VS useLongestHistory: shortHistory is a legacy term that is used in the comparison
    // but it really means that we want to use the longest history available for the item (settings in the comparison)
    // shortHistory was probably named this way to indicate that we would allow a holding of a portfolio to have shorter history than the rest
    const { shortHistory: useLongestHistory, commonInception, showBaseCurrencies: useBaseCurrency } = comparison;

    return comparisonIsReadOnly(comparison) && item.performance?.correlation?.[riskPeriod]?.length
      ? item.performance.correlation[riskPeriod]
      : getCorrelationMatrix(item, riskPeriod, { useLongestHistory, commonInception, useBaseCurrency });
  };

  render() {
    const { item, className, onClick, id, linkDisabled, valueDisclaimer } = this.props;
    const showInlineChart = item.holdings.length <= this.MAX_DISPLAYED_HOLDINGS;

    // If an 'onClick' is set, use that - else prevent event from propagating and selecting comparison card.
    const onChartClick =
      onClick ||
      ((event: React.MouseEvent) => {
        event.stopPropagation();
      });

    return (
      <div className={classNames(className, 'cell correlation-cell')} onClick={onClick} id={id ? `cell-${id}` : undefined}>
        <span className="markup-container">
          {showInlineChart && (
            <>
              {valueDisclaimer && <InfoTooltip title={valueDisclaimer.title} message={valueDisclaimer.message} list={valueDisclaimer.list} />}

              <CorrelationChart onClick={onChartClick} holdings={item.holdings} correlation={this.getCorrelationMatrix()} />
            </>
          )}

          {!showInlineChart && (
            <div className="correlation-not-displayed">
              <FormattedMessage id="Comparison.Correlation.HiddenInfo" />

              <PrimaryLink onClick={this.openModal} disabled={linkDisabled}>
                <FormattedMessage id="Comparison.Correlation.View" />
                <i className="icon-arrow-up-right2" />
              </PrimaryLink>
            </div>
          )}
        </span>
      </div>
    );
  }
}

export default CorrelationCell;
